import React, { useState, useRef, useCallback, useEffect } from "react";
import { X, Pencil, Trash2, FileText, CreditCard, RotateCcw, Target, CheckCircle, Loader2, Banknote, ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useProducts } from "../context/ProductsContext";
import { storePosBooking, SunbedItem } from "../services/posApi";
import { useAuth } from "../auth/AuthContext";
import PaymentCompletedModal from "./PaymentCompletedModal";
import GlobalLoader from "./ui/GlobalLoader";
import { cn } from "../lib/utils";
import { useToast } from "../hooks/useToast";

type SidebarMode = "quick" | "paynow";

interface Service {
  id: string;
  name: string;
  duration: string;
  price: string;
}

interface PosPaymentStepProps {
  selectedService: Service;
  selectedCustomer: string;
  appointmentTime: string;
  tip: number;
  setTip: React.Dispatch<React.SetStateAction<number>>;
  goToFinalPayment: () => void;
  onClose: () => void;
  mode: SidebarMode;
  locationId?: number | string;
  userId?: number | string;
  onPaymentComplete?: (bookingId: number | string) => void;
  // Services data (for appointments/bookings)
  services?: Array<{
    id: string | number;
    quantity?: number;
    price: number;
    taxPercent?: number;
  }>;
  // Sunbeds data (for appointments/bookings)
  sunbeds?: SunbedItem[];
  // Employee assignment
  employees?: (number | string | null)[];
  posDate?: string; // Optional custom date in Y-m-d format
  isEmbedded?: boolean;
  onBack?: () => void;
}

const PosPaymentStep: React.FC<PosPaymentStepProps> = ({
  selectedService,
  selectedCustomer,
  appointmentTime,
  tip,
  setTip,
  goToFinalPayment,
  onClose,
  mode,
  locationId,
  userId,
  onPaymentComplete,
  services,
  sunbeds,
  employees,
  posDate,
  isEmbedded = false,
  onBack,
}) => {
  const toast = useToast();
  const [selectedTip, setSelectedTip] = useState<number>(5);
  const [isPaid, setIsPaid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [completedBookingId, setCompletedBookingId] = useState<number | string | null>(null);
  const { products } = useProducts();
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const paymentProcessedRef = useRef(false);
  const apiCallAttemptedRef = useRef(false); // Track if API call has been attempted

  // Payment calculations
  // Calculate subtotal from products (POS), services (appointments), and sunbeds
  const productsSubtotal = products.reduce(
    (sum, p) => sum + (p.price ?? 0) * (p.quantity ?? 1),
    0
  );
  const servicesSubtotal = services?.reduce(
    (sum, s) => sum + (s.price ?? 0) * (s.quantity ?? 1),
    0
  ) ?? 0;
  // Calculate sunbeds subtotal
  const sunbedsSubtotal = sunbeds?.reduce((sum, sunbed) => {
    // Find selected pricing
    const selectedPricing = sunbed.pricing.find((p: { id: number | string }) => p.id === sunbed.selectedPricingId);
    if (!selectedPricing) return sum;

    // Calculate amount based on pricing type
    if (selectedPricing.type === "fixed") {
      const price = typeof selectedPricing.price === "string"
        ? parseFloat(selectedPricing.price)
        : selectedPricing.price;
      return sum + price;
    } else {
      // per_minute
      const minutes = sunbed.selectedMinutes || 0;
      const pricePerMinute = typeof selectedPricing.price === "string"
        ? parseFloat(selectedPricing.price)
        : selectedPricing.price;
      return sum + (minutes * pricePerMinute);
    }
  }, 0) ?? 0;
  const subtotal = productsSubtotal + servicesSubtotal + sunbedsSubtotal;
  const tax = 0; // Tax can be calculated later if needed
  const total = subtotal + tax;

  // Tendered payments state
  interface TenderedPayment {
    id: string;
    type: "cash" | "cheque" | "card";
    amount: number;
  }
  const [tenderedPayments, setTenderedPayments] = useState<TenderedPayment[]>([]);

  // Calculate totals
  const totalTendered = tenderedPayments.reduce((sum, p) => sum + p.amount, 0);
  const toPay = Math.max(0, total - totalTendered);
  const changeDue = totalTendered > total ? totalTendered - total : 0;

  // Calculator state
  const [paymentAmount, setPaymentAmount] = useState(toPay.toFixed(2));
  const [hasDecimal, setHasDecimal] = useState(false);
  const [decimalDigits, setDecimalDigits] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<"cash" | "cheque" | "card" | null>(null);
  const [isUserTyping, setIsUserTyping] = useState(false);

  // Reset payment processed flag when products change (new transaction)
  React.useEffect(() => {
    paymentProcessedRef.current = false;
    apiCallAttemptedRef.current = false;
  }, [products.length, services?.length]);

  // Update payment amount when toPay changes (only if user is not typing)
  React.useEffect(() => {
    if (!isUserTyping) {
      const newAmount = toPay.toFixed(2);
      setPaymentAmount(newAmount);
      setHasDecimal(false);
      setDecimalDigits(0);
    }
  }, [toPay, isUserTyping]);

  // Calculator keypad handler
  const handleKey = (key: string) => {
    setIsUserTyping(true);
    setPaymentAmount((prev) => {
      let [intPart, decPart] = prev.split(".");
      decPart = decPart || "00";

      // DELETE key
      if (key === "del") {
        if (hasDecimal && decimalDigits > 0) {
          decPart =
            decPart.slice(0, decimalDigits - 1) +
            "0".repeat(2 - (decimalDigits - 1));
          setDecimalDigits(decimalDigits - 1);
          if (decimalDigits - 1 === 0) setHasDecimal(false);
          return `${intPart}.${decPart}`;
        } else if (intPart.length > 1) {
          intPart = intPart.slice(0, -1);
          return `${intPart}.00`;
        } else {
          return "0.00";
        }
      }

      // DECIMAL key
      if (key === ".") {
        if (hasDecimal) return prev;
        setHasDecimal(true);
        setDecimalDigits(0);
        return `${intPart}.`;
      }

      // NUMBER keys
      if (/^\d$/.test(key)) {
        if (!hasDecimal) {
          // Append to integer part
          if (intPart === "0") {
            intPart = key;
          } else {
            intPart += key;
          }
          return `${intPart}.00`;
        } else {
          // Append to decimal part (max 2 digits)
          if (decimalDigits >= 2) return prev;
          const newDec = (decPart.slice(0, decimalDigits) + key).padEnd(2, "0");
          setDecimalDigits(decimalDigits + 1);
          return `${intPart}.${newDec}`;
        }
      }

      // Handle "00" key
      if (key === "00") {
        if (!hasDecimal) {
          if (intPart === "0") {
            return "0.00";
          } else {
            intPart += "00";
            return `${intPart}.00`;
          }
        } else {
          // Can't add 00 to decimal part
          return prev;
        }
      }

      return prev;
    });
  };

  // Handle currency denomination buttons
  const handleDenomination = (amount: number) => {
    setIsUserTyping(true);
    const currentAmount = parseFloat(paymentAmount);
    const newAmount = (currentAmount + amount).toFixed(2);
    setPaymentAmount(newAmount);
    setHasDecimal(false);
    setDecimalDigits(0);
  };

  // Handle remove tendered payment
  const handleRemovePayment = (id: string) => {
    setTenderedPayments(tenderedPayments.filter((p) => p.id !== id));
  };

  // Handle payment method selection - automatically add payment
  const handlePaymentMethodClick = async (method: "cash" | "cheque" | "card") => {
    setSelectedPaymentMethod(method);

    // Get the current payment amount
    let amountToAdd = parseFloat(paymentAmount);

    // If user hasn't typed anything, use remaining balance
    if (!isUserTyping) {
      amountToAdd = toPay;
      setPaymentAmount(toPay.toFixed(2));
      setHasDecimal(false);
      setDecimalDigits(0);
    }

    // Automatically add payment to tendered payments
    if (amountToAdd > 0) {
      // Don't allow adding more than the remaining balance
      if (amountToAdd > toPay) {
        toast.error(`Payment amount cannot exceed remaining balance of ${currencySymbol}${toPay.toFixed(2)}`);
        return;
      }

      const newPayment: TenderedPayment = {
        id: Date.now().toString(),
        type: method,
        amount: amountToAdd,
      };

      setTenderedPayments([...tenderedPayments, newPayment]);
      setIsUserTyping(false);
      // Payment amount will reset to new toPay via useEffect
      // Payment completion will be handled by useEffect watching toPay
    } else if (amountToAdd <= 0) {
      toast.error("Please enter a valid payment amount");
    }
  };

  // Get payment method label
  const getPaymentMethodLabel = (type: string) => {
    switch (type) {
      case "cash":
        return "Cash";
      case "cheque":
        return "Cheque";
      case "card":
        return "Card";
      default:
        return type;
    }
  };

  // Handle payment completion (Pay Later or Part Paid)
  const handlePaymentComplete = useCallback(async (paymentType: "pay_later" | "part_paid") => {
    console.log("handlePaymentComplete called:", { paymentType, isSubmitting, paymentProcessed: paymentProcessedRef.current, apiCallAttempted: apiCallAttemptedRef.current, locationId, userId });

    if (isSubmitting) {
      console.log("Early return: isSubmitting");
      return;
    }

    // Prevent duplicate calls - check both flags
    if (paymentProcessedRef.current || apiCallAttemptedRef.current) {
      console.log("Early return: already processed or API call already attempted");
      return;
    }

    // Mark that we're attempting the API call (set before any async operations)
    apiCallAttemptedRef.current = true;
    paymentProcessedRef.current = true;

    // Validate required fields
    if (!locationId) {
      console.log("Missing locationId");
      // Don't reset flags - API call was attempted, prevent retry
      setIsSubmitting(false);
      toast.error("Location is required. Please select a location.");
      return;
    }

    if (!userId) {
      console.log("Missing userId");
      // Don't reset flags - API call was attempted, prevent retry
      setIsSubmitting(false);
      toast.error("Customer is required. Please select a customer.");
      return;
    }

    setIsSubmitting(true);
    const startTime = Date.now();
    const minProcessingTime = 2000; // Minimum 2 seconds

    try {
      // Prepare service data (for appointments/bookings)
      const serviceIds: (string | number)[] = [];
      const serviceQuantities: number[] = [];
      const servicePrices: number[] = [];
      const serviceTaxPercents: number[] = [];

      if (services && services.length > 0) {
        services.forEach((service) => {
          if (service.id) {
            serviceIds.push(service.id);
            serviceQuantities.push(service.quantity || 1);
            servicePrices.push(service.price || 0);
            serviceTaxPercents.push(service.taxPercent || 0);
          }
        });
      }

      // Prepare product data with tax percentages
      const productIds: (string | number)[] = [];
      const productQuantities: number[] = [];
      const productPrices: number[] = [];
      const productTaxPercents: number[] = [];

      products.forEach((product) => {
        if (product.id && product.quantity && product.price !== undefined) {
          productIds.push(product.id);
          productQuantities.push(product.quantity);
          productPrices.push(product.price);

          // Calculate tax percent for this product
          // If product has taxIds, we need to fetch tax rates, but for now use 0
          // TODO: Fetch actual tax rates from product.taxIds
          const productTaxPercent = 0; // Default to 0, should be calculated from product.taxIds
          productTaxPercents.push(productTaxPercent);
        }
      });

      // Get date and time in multiple formats
      const now = new Date();
      // Use provided posDate or default to current date
      const finalPosDate = posDate || now.toISOString().split('T')[0];

      // Parse components for d-m-Y format
      let day, month, year;
      if (posDate) {
        const parts = posDate.split('-');
        year = parts[0];
        month = parts[1];
        day = parts[2];
      } else {
        day = String(now.getDate()).padStart(2, '0');
        month = String(now.getMonth() + 1).padStart(2, '0');
        year = now.getFullYear();
      }
      const dateDMY = `${day}-${month}-${year}`;

      // Use appointmentTime prop for time if available, otherwise current time
      const finalPosTime = appointmentTime || (() => {
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';
        const displayHours = hours % 12 || 12;
        return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
      })();

      // Format payments array
      const payments = tenderedPayments.map((payment) => ({
        type: payment.type,
        amount: payment.amount,
      }));

      // Determine payment type
      const isPayLater = paymentType === "pay_later" || tenderedPayments.length === 0;
      const type = isPayLater ? "pay_later" : "part_paid";

      // Determine payment gateway (Multiple if more than one payment method)
      const paymentGateway = payments.length > 1 ? "Multiple" : (payments[0]?.type.charAt(0).toUpperCase() + payments[0]?.type.slice(1) || "Cash");

      // Calculate total tax (services + products)
      // Note: Backend calculates tax from ItemTax table, but we send tax_percent for each item
      // For now, we'll send 0 and let backend calculate, or we can calculate if we have tax data
      const calculatedTotalTax = tax; // This should be calculated from services and products if we have tax data

      // Helper to ensure time is in H:i a format
      const formatToHiA = (timeStr: string): string => {
        if (!timeStr) return "";

        // Handle ISO string
        if (timeStr.includes('T') && timeStr.includes('Z')) {
          const date = new Date(timeStr);
          if (!isNaN(date.getTime())) {
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const ampm = hours >= 12 ? 'pm' : 'am';
            const displayHours = hours % 12 || 12;
            return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
          }
        }

        // Handle range like "9.30–10.30 am"
        if (timeStr.includes('–')) {
          const startPart = timeStr.split('–')[0].trim();
          const timeParts = startPart.split('.');
          const hours = timeParts[0].padStart(2, '0');
          const minutes = (timeParts[1] || '00').padEnd(2, '0');
          const ampm = timeStr.toLowerCase().includes('pm') ? 'pm' : 'am';
          return `${hours}:${minutes} ${ampm}`;
        }

        return timeStr;
      };

      const finalFormattedTime = formatToHiA(finalPosTime);

      // Prepare payload matching backend format
      const payload = {
        // Services (if provided)
        cart_services: serviceIds.length > 0 ? serviceIds : undefined,
        cart_quantity: serviceQuantities.length > 0 ? serviceQuantities : undefined,
        cart_prices: servicePrices.length > 0 ? servicePrices : undefined,
        tax_percent: serviceTaxPercents.length > 0 ? serviceTaxPercents : undefined,
        // Products (if provided)
        product_cart_services: productIds.length > 0 ? productIds : undefined,
        product_cart_quantity: productQuantities.length > 0 ? productQuantities : undefined,
        product_cart_prices: productPrices.length > 0 ? productPrices : undefined,
        product_tax_percent: productTaxPercents.length > 0 ? productTaxPercents : undefined,
        // Sunbeds (if provided)
        calendar_sunbeds: sunbeds && sunbeds.length > 0 ? sunbeds : undefined,
        location: locationId,
        pos_date: finalPosDate, // Y-m-d format
        pos_time: finalFormattedTime, // H:i a format
        date: dateDMY, // d-m-Y format (alternative)
        time: finalFormattedTime, // H:i a format (alternative)
        posTime: finalFormattedTime, // H:i a format (alternative)
        user_id: userId,
        payments: payments.length > 0 ? payments : [{ type: "cash" as const, amount: 0 }],
        payment_gateway: paymentGateway,
        pending_amount: toPay,
        amount_paid: totalTendered,
        change_due: changeDue.toFixed(2),
        type: type,
        serviceType: "offline" as const,
        total_tax: calculatedTotalTax,
        cart_discount: 0,
        coupon_id: "",
        coupon_amount: "",
        coupon: "",
        uuid: null,
        transaction_id: null,
        employee: employees || undefined,
      };

      // Call API
      let response;
      try {
        response = await storePosBooking(payload);
        console.log("Payment API Response:", response);
      } catch (apiError) {
        console.error("API call failed:", apiError);
        throw apiError; // Re-throw to be caught by outer catch
      }

      // Calculate elapsed time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minProcessingTime - elapsedTime);

      // If we get here, API call was successful - show success modal
      const bookingId = response?.booking?.id;
      console.log("Payment API call successful, booking ID:", bookingId);
      console.log("Payment API Response:", response);
      console.log("Will show success modal after", remainingTime, "ms");

      // Store API response and booking ID
      setApiResponse(response);
      setCompletedBookingId(bookingId);

      // Wait for minimum processing time before showing success
      setTimeout(() => {
        console.log("Minimum processing time elapsed, hiding processing modal");
        // Success - hide processing modal first
        setIsSubmitting(false);

        // Delay to allow processing modal to fade out before showing success modal
        setTimeout(() => {
          console.log("Showing success modal now");
          // Show success modal with payment summary
          setShowSuccessModal(true);
          // Don't call onPaymentComplete here - wait for user to click Done button
        }, 500);
      }, remainingTime);
    } catch (error: any) {
      console.error("Error processing payment:", error);
      // Calculate elapsed time for error case
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minProcessingTime - elapsedTime);

      // Wait for minimum processing time before showing error
      setTimeout(() => {
        setIsSubmitting(false);
        // Don't reset paymentProcessedRef or apiCallAttemptedRef - API call was attempted once
        // This prevents automatic retries and duplicate API calls
        toast.error(error.response?.data?.message || "Failed to process payment. Please try again.");
      }, remainingTime);
    }
  }, [locationId, userId, products, services, employees, tenderedPayments, total, tax, totalTendered, toPay, changeDue, onPaymentComplete, onClose, isSubmitting]);


  // Auto-process payment when fully paid (toPay becomes 0)
  React.useEffect(() => {
    console.log("Payment check:", { toPay, tenderedPaymentsLength: tenderedPayments.length, isSubmitting, paymentProcessed: paymentProcessedRef.current, apiCallAttempted: apiCallAttemptedRef.current });

    // Only call API if payment is complete AND API hasn't been attempted yet
    if (toPay === 0 && tenderedPayments.length > 0 && !isSubmitting && !paymentProcessedRef.current && !apiCallAttemptedRef.current) {
      console.log("Payment complete! Calling API...");
      // Payment is complete, automatically process
      handlePaymentComplete("part_paid").catch((error) => {
        console.error("Payment processing error:", error);
        // Don't reset flags - API call was attempted once, prevent retry
      });
    }
  }, [toPay, tenderedPayments.length, isSubmitting, handlePaymentComplete]);

  return (
    <>
      <div
        className={isEmbedded
          ? "h-full w-full bg-[#F9F9F9] flex flex-col lg:flex-row relative"
          : "fixed top-0 right-0 h-full w-full lg:w-[60%] bg-[#F9F9F9] shadow-xl border-l border-gray-200 transform transition-transform duration-300 z-[1000] flex flex-col lg:flex-row"
        }
      >
        {/* Close Button - Only show if not embedded (parent handles it otherwise) */}
        {!isEmbedded && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute lg:-left-14 top-4 lg:top-8 right-4 lg:right-auto w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg lg:shadow-[-4px_0_12px_rgba(0,0,0,0.15)] hover:bg-[#402B69] hover:text-white transition-all z-[60] group"
            aria-label="Close"
          >
            <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
          </motion.button>
        )}

        {/* Left Side: Select Payment Methods */}
        <div className="w-full lg:w-[30%] p-6 sm:p-8 relative bg-white border-b lg:border-b-0 lg:border-r border-gray-100 flex-shrink-0 overflow-y-auto">
          {isEmbedded && onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-500 hover:text-[#885ABB] transition-colors mb-6 font-semibold text-sm"
            >
              <ChevronLeft size={16} />
              <span>Back</span>
            </button>
          )}

          <div className="mb-6 sm:mb-10">
            <h3 className="text-[18px] sm:text-[20px] font-bold text-[#402B69] tracking-tight">
              Billing Methods
            </h3>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Select Gateway</p>
          </div>

          <div className="grid grid-cols-3 lg:flex lg:flex-col gap-3">
            {[
              { id: "cash", label: "Cash", icon: Banknote },
              { id: "cheque", label: "Cheque", icon: FileText },
              { id: "card", label: "Card", icon: CreditCard }
            ].map((method) => {
              const Icon = method.icon;
              const isSelected = selectedPaymentMethod === method.id;
              return (
                <button
                  key={method.id}
                  onClick={() => handlePaymentMethodClick(method.id as any)}
                  className={cn(
                    "w-full py-3 sm:py-4 px-3 sm:px-5 rounded-xl font-bold transition-all duration-300 flex flex-col lg:flex-row items-center lg:gap-4 text-[10px] sm:text-sm",
                    isSelected
                      ? "bg-[#885ABB]/5 text-[#885ABB] border-2 border-[#885ABB] shadow-sm scale-[1.02]"
                      : "bg-white text-gray-500 border border-gray-100 hover:border-[#885ABB]/30 hover:bg-gray-50"
                  )}
                >
                  <div className={cn(
                    "p-1.5 sm:p-2 rounded-lg transition-colors mb-1 lg:mb-0",
                    isSelected ? "bg-[#885ABB]/10 text-[#885ABB]" : "bg-gray-50 text-gray-400"
                  )}>
                    <Icon size={20} />
                  </div>
                  <span>{method.label}</span>
                </button>
              );
            })}
          </div>

          {/* Divider - Desktop Only */}
          <div className="hidden lg:block absolute top-0 right-0 h-full w-[2px] bg-gray-200 shadow-[4px_0_12px_rgba(0,0,0,0.15)]"></div>
        </div>

        {/* Right Side: Calculator and Summary */}
        <div className="w-full lg:w-[70%] flex flex-col flex-1 bg-[#F9F9F9] overflow-hidden">
          {/* Top Section - Calculator (Scrollable area) */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar">
            {/* Amount Display */}
            <div className="mb-2">
              <div className="text-[48px] font-bold text-[#402B69] text-center">
                {currencySymbol} {paymentAmount}
              </div>
            </div>

            {/* Calculator Section */}
            <div className="flex justify-center mb-6 lg:mb-8">
              <div className="flex flex-col lg:flex-row gap-4 px-2">
                {/* Numeric Keypad */}
                <div className="grid grid-cols-3 gap-2 flex-shrink-0">
                  {["1", "2", "3", "4", "5", "6", "7", "8", "9", "00", "0", "."].map(
                    (k) => (
                      <button
                        key={k}
                        onClick={() => handleKey(k)}
                        className="h-14 w-16 sm:w-20 bg-white border border-gray-100 rounded-xl text-2xl font-bold text-[#402B69] hover:border-[#885ABB] hover:text-[#885ABB] transition-all active:scale-95 shadow-sm"
                      >
                        {k}
                      </button>
                    )
                  )}
                </div>

                {/* Right Side: Denominations and Action Buttons */}
                <div className="grid grid-cols-2 gap-2 flex-shrink-0">
                  {[1, 5, 10, 20, 30, 50].map(val => (
                    <button
                      key={val}
                      onClick={() => handleDenomination(val)}
                      className="h-14 w-16 sm:w-[72px] bg-[#885ABB]/5 border border-[#885ABB]/10 text-[#885ABB] rounded-xl text-sm font-black hover:bg-[#885ABB] hover:text-white transition-all shadow-sm"
                    >
                      {currencySymbol}{val}
                    </button>
                  ))}
                  {/* Target Button - White with purple border */}
                  <button
                    onClick={() => {
                      setIsUserTyping(false);
                      setPaymentAmount(toPay.toFixed(2));
                      setHasDecimal(false);
                      setDecimalDigits(0);
                    }}
                    className="h-14 w-16 sm:w-[72px] bg-white border-2 border-[#885ABB]/20 rounded-xl flex items-center justify-center hover:bg-[#885ABB] hover:text-white transition-all shadow-sm group"
                    title="Reset to To Pay"
                  >
                    <Target className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </button>
                  {/* X Button - Purple with white icon */}
                  <button
                    onClick={() => handleKey("del")}
                    className="h-14 w-16 sm:w-[72px] bg-red-50 border-2 border-red-50 text-red-500 rounded-xl flex items-center justify-center hover:bg-red-500 hover:text-white transition-all active:scale-95 shadow-sm group"
                  >
                    <X className="w-5 h-5 stroke-[3]" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Area Area - Matches SummaryStep */}
          <div className="bg-white shadow-[0_-10px_30px_rgba(0,0,0,0.03)] border-t border-gray-100 flex flex-col w-full z-10 flex-shrink-0 sticky bottom-0 lg:static">
            {/* Order Summary Section */}
            <div className="px-4 sm:px-6 pt-4 pb-2">
              <section className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm space-y-3">
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-gray-500 font-medium">
                    <span>Subtotal</span>
                    <span>{currencySymbol}{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Taxes & Fees</span>
                    <span>{currencySymbol}{tax.toFixed(2)}</span>
                  </div>
                  <div className="pt-3 border-t border-gray-50 flex justify-between items-center mb-1">
                    <span className="text-sm sm:text-base font-[600] text-[#402B69]">Total</span>
                    <span className="text-lg font-bold text-[#885ABB]">{currencySymbol}{total.toFixed(2)}</span>
                  </div>

                  {/* Tendered Payments - Only show if there are tenders */}
                  {tenderedPayments.length > 0 && (
                    <div className="pt-2 border-t border-gray-50 space-y-1">
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Tendered Payments</div>
                      {tenderedPayments.map((payment) => (
                        <div
                          key={payment.id}
                          className="flex justify-between items-center text-xs py-1"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">
                              {getPaymentMethodLabel(payment.type)}
                            </span>
                            <button
                              onClick={() => handleRemovePayment(payment.id)}
                              className="p-1 rounded-md text-[#885ABB] hover:text-white hover:bg-[#885ABB] transition-all duration-200"
                              title="Remove payment"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                          <span className="font-semibold text-[#402B69]">
                            {currencySymbol}{payment.amount.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* To Pay Section */}
                  <div className="pt-3 border-t border-gray-50 flex justify-between items-center">
                    <span className="text-sm sm:text-base font-bold text-[#402B69]">To Pay</span>
                    <span className="text-lg sm:text-xl font-black text-[#885ABB]">{currencySymbol}{toPay.toFixed(2)}</span>
                  </div>
                </div>
              </section>
            </div>

            {/* Action Buttons */}
            {tenderedPayments.length > 0 && (
              <div className="p-4 sm:p-6 pt-2 pb-8 sm:pb-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={() => handlePaymentComplete("part_paid")}
                  disabled={isSubmitting}
                  className="flex-1 h-12 bg-[#885ABB] text-white rounded-xl font-bold hover:bg-[#7246a0] transition-all text-xs sm:text-sm uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-[#885ABB]/20"
                >
                  {isSubmitting ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      <span>Post Transaction</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div >

      {/* Processing Modal - Rendered outside drawer */}
      {
        isSubmitting && (
          // <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] animate-fadeIn">
          //   <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-[90%] mx-4 transform transition-all duration-300 animate-scaleIn">
          //     {/* Spinner Icon with Animation */}
          //     <div className="flex justify-center mb-6">
          //       <div className="relative">
          //         <Loader2 className="w-16 h-16 text-[#885ABB] animate-spin" />
          //       </div>
          //     </div>

          //     {/* Processing Message */}
          //     <div className="text-center mb-6">
          //       <h2 className="text-3xl font-bold text-[#402B69] mb-3 animate-slideUp">
          //         Processing Payment...
          //       </h2>
          //       <p className="text-lg text-gray-600 animate-slideUp delay-100">
          //         Please wait while we process your payment
          //       </p>
          //     </div>

          //     {/* Animated Dots */}
          //     <div className="flex justify-center gap-2">
          //       <div className="w-4 h-4 bg-[#885ABB] rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
          //       <div className="w-4 h-4 bg-[#885ABB] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          //       <div className="w-4 h-4 bg-[#885ABB] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          //     </div>
          //   </div>
          // </div>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] animate-fadeIn backdrop-blur-[2px]">
            <GlobalLoader
              initialPageLoad={false}
              description="Processing Payment..."
              compensateSidebar={false}
            />
          </div>
        )
      }

      {/* Success Modal - Rendered outside drawer */}
      <PaymentCompletedModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          // Close drawer after a short delay
          setTimeout(() => {
            onClose();
          }, 300);
        }}
        apiResponse={apiResponse}
        totalTendered={totalTendered}
        changeDue={changeDue}
        bookingId={completedBookingId}
        onPaymentComplete={onPaymentComplete}
      />
    </>
  );
};

export default PosPaymentStep;